import React from 'react';

import Store from 'store';
import Locale from 'data/locale';

import Select from 'ui/select';

import NumberInput from 'ui/number';
import EditingContext from 'store/editingContext';
import { profileHasImports } from 'logic/profileReducers';
import Data from 'data';
import { compositeStats, optimizeItems, skillCompositeId, skillCompositeStat } from 'logic/optimizer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import EquipmentSection from './Section';

const _L = Locale("ui-equipment.js", "ui-skills.js", "skilldata");

function PrioritySelect({ options, value, onChange }) {
  const state = Store.useState();
  return (
    <select value={value || ""} onChange={e => onChange(e.target.value)}>
      {options(state).map(({id, name}) => <option key={id} value={id}>{name}</option>)}
    </select>
  );
}

function PriorityItem({ index, item }) {
  const dispatch = Store.useDispatch();
  const skills = Store.useProfile(profile => profile.skills);
  const limitStats = Data.useOption("limitStats");
  const follower = (EditingContext.useStateView("paperdollMode") === "follower");
  const charClass = Store.useProfile(profile => follower ? profile.follower : profile.class);

  const stat = item.stat;
  const composite = React.useMemo(() => {
    if (compositeStats[stat] && (compositeStats[stat].follower == null || compositeStats[stat].follower === follower)) {
      return compositeStats[stat];
    }
    for (const skill of skills) {
      if (skill && skillCompositeId(charClass, skill[0]) === stat) {
        return skillCompositeStat(charClass, skill[0]);
      }
    }
  }, [charClass, skills, stat, follower]);

  const getOptions = React.useCallback(() => {
    const results = [];
    results.push({
      title: _L`Composite Stats`,
      options: Object.entries(compositeStats).filter(([, stat]) => stat.follower == null || stat.follower === follower).map(([id, stat]) => ({id, name: stat.name()})),
    });

    if (skills.some(Boolean) && !follower) {
      results.push({
        title: _L`Skill Values`,
        options: skills.filter(Boolean).map(skill => ({id: skillCompositeId(charClass, skill[0]), name: Data.skills[charClass][skill[0]].name})),
      });
    }

    for (const [catType, catList] of Object.entries(Data.statList)) {
      for (const [cat, list] of Object.entries(catList)) {
        const options = Data.extendStats([], list).filter(stat => {
          if (Data.stats[stat].caldesanns) return false;
          if (limitStats && Data.stats[stat].classes && !Data.stats[stat].classes.includes(charClass)) return false;
          return true;
        }).map(stat => ({id: stat, name: Data.stats[stat].name}));
        if (options.length) {
          results.push({
            title: cat + (catType === "secondary" ? _L` (Secondary)` : ""),
            options,
          });
        }
      }
    }

    return results;
  }, [limitStats, charClass, skills, follower]);

  const value = React.useMemo(() => stat && {id: stat, name: (composite && composite.name()) || Data.stats[stat]?.name || _L`Unknown`}, [composite, stat]);
  const onRemove = React.useCallback(() => dispatch.profile.setStatPriority(prio => {
    prio = prio.slice();
    prio.splice(index, 1);
    return prio;
  }), [dispatch, index]);
  const onChange = React.useCallback(value => dispatch.profile.setStatPriority(prio => {
    prio = prio.slice();
    prio[index] = {stat: value.id, options: {}, id: prio[index].id};
    return prio;
  }), [dispatch, index]);

  const setOption = React.useCallback((id, value) => dispatch.profile.setStatPriority(prio => {
    prio = prio.slice();
    prio[index] = {...prio[index]};
    prio[index].options = {...prio[index].options};
    prio[index].options[id] = value;
    return prio;
  }), [dispatch, index]);

  if (!composite && !Data.stats[stat] && stat) return null;

  return (
    <Draggable draggableId={item.id.toString()} index={index}>
      {provided => (
        <li {...provided.draggableProps} ref={provided.innerRef}>
          <div className="d3p-priority-header" {...provided.dragHandleProps}>
            <span className="d3p-priority-remove" onClick={onRemove}>
              <FontAwesomeIcon icon={faTimes}/>
            </span>
            <Select options={getOptions}
                    value={value}
                    onChange={onChange}
                    searchLimit={10}
                    searchMiddle={true}
                    placeholder={_L`Choose Stat`}
            />
          </div>
          {!!(composite && Object.keys(composite.options).length) && (
            <div className="d3p-priority-options">
              {Object.entries(composite.options).map(([id, option]) => {
                switch (option.type) {
                case "checkbox":
                  return <label key={id}><input type="checkbox" checked={!!item.options[id]} onChange={e => setOption(id, e.target.checked)}/>{option.name()}</label>;
                case "select":
                  return <PrioritySelect key={id} options={option.options} value={item.options[id]} onChange={value => setOption(id, value)}/>;
                default:
                  return null;
                }
              })}
            </div>
          )}
        </li>
      )}
    </Draggable>
  );
}

export default React.memo(function EquipmentOptimizer() {
  const [unlock, setUnlock] = EditingContext.useState("equipmentOptimizerUnlock", false);
  const [useGift, setUseGift] = EditingContext.useState("equipmentOptimizerGift", true);
  const [perfection, setPerfection] = EditingContext.useState("equipmentOptimizerPerfection", 100);
  const follower = (EditingContext.useStateView("paperdollMode") === "follower");
  const onSetUnlock = React.useCallback(e => setUnlock(e.target.checked), [setUnlock]);
  const onSetUseGift = React.useCallback(e => setUseGift(e.target.checked), [setUseGift]);

  const hasImports = Store.useProfile(profile => profileHasImports(profile), []);

  const priority = Store.useProfile(profile => profile.statPriority);

  const dispatch = Store.useDispatch();
  const store = Store.useStore();
  const onOptimize = React.useCallback(() => {
    const state = store.getState();
    const items = optimizeItems(state, follower, {unlock, useGift, perfection});
    dispatch.setProfile(profile => ({...profile, [follower ? "followerItems" : "items"]: items}));
  }, [dispatch, store, unlock, useGift, perfection, follower]);

  const onAdd = React.useCallback(() => dispatch.profile.setStatPriority(prio => {
    const maxId = prio.reduce((prev, prio) => Math.max(prev, prio.id || 0), 0);
    return [...prio, {stat: null, options: {}, id: maxId + 1}];
  }), [dispatch]);

  const onDragEnd = React.useCallback(({ destination, source }) => {
    if (!destination || source.index === destination.index) return;
    dispatch.profile.setStatPriority(prio => {
      prio = prio.slice();
      const items = prio.splice(source.index, 1);
      prio.splice(destination.index, 0, ...items);
      return prio;
    });
  }, [dispatch]);

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <EquipmentSection id="equipmentOptimizerExpanded" title={_L`Stat priority`}>
        <div className="d3p-EquipmentOptimizer">
          <p className="d3p-tip">{_L`For best results, assign paragon, Caldesanns enchants and skills/passives before using the optimizer.`}</p>
          {hasImports && <div><label><input type="checkbox" checked={unlock} onChange={onSetUnlock}/>{_L`Unlock imported items`}</label></div>}
          <div><label><input type="checkbox" checked={useGift} onChange={onSetUseGift}/>{_L`Use Ramaladni's Gift`}</label></div>
          <div><label>{_L`Set stat values to ${<NumberInput min={0} max={100} value={perfection} onChange={setPerfection}/>}%`}</label></div>

          <Droppable droppableId="priority" ignoreContainerClipping>
            {provided => (
              <ul className="d3p-EquipmentStatPriority" ref={provided.innerRef} {...provided.droppableProps}>
                {priority.map((prio, index) => <PriorityItem key={prio.id} index={index} item={prio}/>)}
                {provided.placeholder}
                <li onClick={onAdd} className="d3p-priority-add">
                  <span><FontAwesomeIcon icon={faPlus}/></span>
                  <span>{_L`Add stat`}</span>
                </li>
              </ul>
            )}
          </Droppable>

          <div className="d3p-optimize" onClick={onOptimize}>{_L`Optimize stats`}</div>
        </div>
      </EquipmentSection>
    </DragDropContext>
  );
});
